import {
    type TablerIcon,
    IconBrandFacebook,
    IconBrandX,
    IconBrandReddit,
    IconBrandYoutubeFilled,
    IconBrandInstagram,
    IconRss,
    IconBrandLinkedin,
} from '@tabler/icons-react'
import { Mail, Link, type LucideIcon } from 'lucide-react'
import { CopyLink } from '@/components/frontend/CopyLink'
import NextLink from 'next/link'
import { cn } from '@/utilities/ui'
import { type ClassValue } from 'clsx'

export type SocialLinks = Partial<Record<SupportedSocials, string>>
export type SupportedSocials = 'twitter' | 'facebook' | 'reddit' | 'email' | 'link' | 'youtube' | 'instagram' | 'rss' | 'linkedin'

interface SocialMetadata {
    label: string
    icon: TablerIcon | LucideIcon
    bgClass: string
}

const socials: Record<SupportedSocials, SocialMetadata> = {
    twitter: {
        label: 'Twitter',
        icon: IconBrandX,
        bgClass: 'bg-[#00A3FF]',
    },
    facebook: {
        label: 'Facebook',
        icon: IconBrandFacebook,
        bgClass: 'bg-[#1564C5]',
    },
    reddit: {
        label: 'Reddit',
        icon: IconBrandReddit,
        bgClass: 'bg-[#FF5700]',
    },
    email: {
        label: 'Email',
        icon: Mail,
        bgClass: 'bg-[#757575]',
    },
    link: {
        label: 'Link',
        icon: Link,
        bgClass: 'bg-[#0075B2]',
    },
    instagram: {
        label: 'Instagram',
        icon: IconBrandInstagram,
        bgClass: 'bg-[#C32AA3]',
    },
    linkedin: {
        label: 'LinkedIn',
        icon: IconBrandLinkedin,
        bgClass: 'bg-[#0A66C2]',
    },
    youtube: {
        label: 'Youtube',
        icon: IconBrandYoutubeFilled,
        bgClass: 'bg-[#EA3323]',
    },
    rss: {
        label: 'RSS Feed',
        icon: IconRss,
        bgClass: 'bg-[#7D7D7D]',
    },
}

export interface SocialProps {
    size?: number
    links: SocialLinks
    variant?: 'monochrome' | 'custom' | 'color' | 'accent' | 'list'
    classes?: Partial<Record<'link' | 'icon' | 'container', ClassValue>>
    useShareToSocial?: boolean
}

export function Socials({ links, variant = 'color', useShareToSocial, classes, size }: SocialProps) {
    // Render a list for socials
    if (variant === 'list') {
        return (
            <ul className={cn(classes?.container)}>
                {Object.entries(links).map(([id, link]) => {
                    const meta = socials[id as SupportedSocials]
                    const LinkComponent = id === 'link' ? CopyLink : NextLink
                    const title = useShareToSocial ? `Share to ${meta.label}` : meta.label
                    const dataProps = useShareToSocial
                        ? { 'data-vtas-click': 'inner:ShareContent:share-tools-buttons-top', 'data-ga': `[["Share Tools","Share to ${meta.label}","top"]]` }
                        : { 'data-ga': `[["Network navigation","Social button click","${id}",{"metric21":1}]]` }

                    return (
                        <li key={id}>
                            <LinkComponent href={link} target='_blank' className={cn(classes?.link)} title={title} aria-label={title} {...dataProps}>
                                {title}
                            </LinkComponent>
                        </li>
                    )
                })}
            </ul>
        )
    }

    // Render icons for socials
    return (
        <div className={cn('flex flex-row flex-nowrap gap-2 group', classes?.container)}>
            {Object.entries(links).map(([id, link]) => {
                const isFacebook = id === 'facebook'
                const meta = socials[id as SupportedSocials]
                const LinkComponent = id === 'link' ? CopyLink : NextLink
                const title = useShareToSocial ? `Share to ${meta.label}` : meta.label
                const dataProps = useShareToSocial
                    ? { 'data-vtas-click': 'inner:ShareContent:share-tools-buttons-top', 'data-ga': `[["Share Tools","Share to ${meta.label}","top"]]` }
                    : { 'data-ga': `[["Network navigation","Social button click","${id}",{"metric21":1}]]` }

                return (
                    <LinkComponent
                        key={id}
                        href={link}
                        target='_blank'
                        className={cn(
                            'rounded-full p-2 transition duration-300 ease-in-out',
                            {
                                [meta.bgClass]: variant === 'color',
                                'bg-black': isFacebook && variant === 'monochrome',
                                'bg-transparent': !isFacebook && variant === 'monochrome',
                                'hover:bg-gray-200': variant === 'monochrome',
                                'hover:bg-gray-600': variant === 'monochrome' && isFacebook,
                                'hover:brightness-[80%]': variant !== 'monochrome',
                            },
                            classes?.link,
                        )}
                        title={title}
                        aria-label={title}
                        {...dataProps}
                    >
                        <meta.icon
                            size={size}
                            className={cn(
                                {
                                    'stroke-white': variant !== 'monochrome',
                                    'stroke-black': variant === 'monochrome' && !isFacebook,
                                    'fill-white': isFacebook,
                                    'stroke-0': isFacebook,
                                    'hover:stroke-black stroke-accent': variant === 'accent' && !isFacebook,
                                    'hover:fill-black fill-accent': variant === 'accent' && isFacebook,
                                },
                                classes?.icon,
                            )}
                            aria-hidden='true'
                        />
                        <span className='sr-only'>{title}</span>
                    </LinkComponent>
                )
            })}
        </div>
    )
}
